import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { useEffect, useRef, useState } from "react";
import { AxiosError } from "axios";
import { useStore } from "src/Shared/Hooks";
import { CommonService, ToastService } from "src/Shared/Services";
import CompanyService from "../../Services/Company.service";
import { IOnboardingWizardState } from "../../Interfaces/IOnboardingWizardState";
import { step1FieldErrors, stepValidationMessage } from "./OnboardingCapture.validation";
import { IUpdateClientDetails } from "../../Interfaces/IUpdateClientDetails";
import { COUNTRY_OPTIONS } from "../../Enums/Country.enum";
import {
  clientTypesForCountry,
  COMPANY_CODE_FORMAT_MESSAGE,
  isCompanyCodeFormatValid,
  normaliseCompanyCode,
  upperCaseAsTyped,
} from "./OnboardingCapture.utils";
import { useWriteThrough, WriteThroughResult, combineWriteResults } from "./useWriteThrough";
import ImperansReferencePicker from "../ImperansReference/ImperansReferencePicker";
import { useImperansReferenceList } from "../ImperansReference/useImperansReferenceList";
import {
  franchiseSuggestedByRegion,
  pickFromOption,
} from "../ImperansReference/ImperansReference.rules";
import { IImperansReferencePick } from "../../Interfaces/IImperansReference";

type Props = {
  state: IOnboardingWizardState;
  companyRowID: string;
  frozen: boolean;
  registerFlush: (flush: () => Promise<WriteThroughResult>) => void;
  /** Lets the shell ask this step whether it is valid before it writes anything (bug 5846). */
  registerValidate?: (validate: (() => string | null) | null) => void;
  /**
   * Set once an advance has been refused, so the fields that are blocking it are marked and each
   * says why. Nothing is marked before the attempt — a pristine form is not a form with errors
   * (bug 5668).
   */
  showFieldErrors?: boolean;
};

type RegionOption = { value: number; label: string; countryName: string };

/**
 * Wizard step 1 — Client Information (stories 5596–5598).
 *
 * Every field here is a client-record field, saved through the record's own instant-save endpoint,
 * so a value edited in the wizard and the same value edited on Details are one stored value (P.6).
 * Only the Client Type lives on the onboarding, mirrored to the CRM's National/Regional field
 * server-side (S1.8; one value since D12, story 6348).
 *
 * No address, town, post code or postal field appears anywhere — every address is a step-3 site,
 * the first doubling as head office (S1.4).
 */
const Step1ClientInformation: React.FC<Props> = ({
  state,
  companyRowID,
  frozen,
  registerFlush,
  registerValidate,
  showFieldErrors,
}) => {
  /**
   * Set the moment a save is attempted, rather than only once the shell's own verdict comes back.
   *
   * `showFieldErrors` arrives from the shell, which sets it inside `advance()`'s callback — after a
   * write-through POST and a full state GET have both resolved. Every rule below is answerable from
   * what is already on screen (four presence checks and one regex), so waiting two round trips to say
   * "Enter or generate a company code." made a basic required-field error feel broken.
   */
  const [saveAttempted, setSaveAttempted] = useState<boolean>(false);
  const {
    rootStore: {
      commonStore: {
        supportiveRegions,
        industries,
        leadTypeList,
        loadCommonData,
      },
    },
  } = useStore();

  // Local state is the source of truth while editing; it is written through on a short debounce.
  const [form, setForm] = useState({
    companyName: state.companyName || "",
    companyCode: state.companyCode || "",
    countryCode: state.countryCode || "",
    subDivisionCode: state.subDivisionCode || "",
    headOfficePhone: state.headOfficePhone || "",
    franchiseID: state.franchiseID ?? "",
    franchiseOfficeID: state.franchiseOfficeID ?? "",
    // The owning Imperans franchise, picked from the paired Imperans's list (story 6372). The CRM
    // region above is a different value and only ever suggests this one.
    imperansFranchiseID: (state.imperansFranchiseID ?? null) as number | null,
    imperansFranchiseName: (state.imperansFranchiseName ?? null) as string | null,
    industryID: state.industryID ?? "",
    leadTypeID: state.leadTypeID ?? "",
    website: state.website || "",
    totalStaffNumber: state.totalStaffNumber ?? "",
    specialPricing: Boolean(state.specialPricing),
    // One Client Type since D12 (story 6348): the payload's value, or the CRM column it mirrors.
    clientType: state.clientInformation?.clientType ?? state.companyType ?? "",
  });
  /**
   * The step's own rules, read from the live form rather than the shell's `state` snapshot.
   *
   * Same single rule set (`step1FieldErrors`) the shell validates with — only the values differ:
   * `state` is refreshed by an explicit reload, so between a keystroke and the next reload it holds
   * the previous value, which is exactly why validating it directly used to fail the first click
   * after any edit (bugs 5667, 5673, 5713). `form` is updated on every keystroke, so it can never be
   * behind. The relationship manager is read-only here (Details owns it), so `state` is right for it.
   *
   * "Please fix the highlighted fields" is only honest if something is highlighted (bug 5668), so
   * nothing is marked until a save has actually been attempted.
   */
  const liveState: IOnboardingWizardState = {
    ...state,
    companyName: form.companyName,
    companyCode: form.companyCode,
    countryCode: form.countryCode,
    franchiseID: form.franchiseID === "" ? null : Number(form.franchiseID),
    imperansFranchiseID: form.imperansFranchiseID,
    industryID: form.industryID === "" ? null : Number(form.industryID),
    clientInformation: { ...state.clientInformation, clientType: form.clientType || null },
    // Mirrored from the live value the same way the server mirrors the stored value to CompanyType
    // (S1.8). Client Type counts as present from either store, so leaving the loaded CompanyType in
    // place here would keep reading "present" for the whole debounce after it was cleared.
    companyType: form.clientType || null,
  } as IOnboardingWizardState;
  // The form as last rendered, for the write-through. A debounced write runs the closure of the
  // render that scheduled it, so reading `form` there sent the values of that render — a franchise
  // pick made while the refreshed code was still on its way sent the old code back 600ms later, and
  // the server took it for a hand-typed change (TC-6348-03).
  const formRef = useRef(form);
  formRef.current = form;
  const liveFieldErrors = step1FieldErrors(liveState);
  const fieldErrors = showFieldErrors || saveAttempted ? liveFieldErrors : {};

  // Read by the validator below, which the shell holds across renders: a ref is the only way for it
  // to see the values as they are now rather than as they were when it was registered.
  const liveStateRef = useRef<IOnboardingWizardState>(liveState);
  liveStateRef.current = liveState;

  const [codeMessage, setCodeMessage] = useState<string>("");
  const [regions, setRegions] = useState<RegionOption[]>([]);
  // Mirrors state.isCompanyCodeAutoGenerated, but updated the instant it changes rather than only
  // on the next step reload — state is a mount-time snapshot, so relying on it directly let a
  // hand-typed code keep regenerating for the rest of the visit (bug 5819).
  const [autoGenerated, setAutoGeneratedState] = useState<boolean>(
    Boolean(state.isCompanyCodeAutoGenerated),
  );
  // The same flag for an answer already on its way: a refresh's response runs the closure of the
  // render that asked for it, so only a ref can tell it the code was typed by hand meanwhile (S1.3).
  const autoGeneratedRef = useRef<boolean>(autoGenerated);
  const setAutoGenerated = (value: boolean) => {
    autoGeneratedRef.current = value;
    setAutoGeneratedState(value);
  };

  useEffect(() => {
    loadCommonData();
    CommonService.getRegions().subscribe({
      next: (data) => setRegions((data as any) || []),
      error: () => setRegions([]),
    });
  }, []);

  const record = useWriteThrough<Partial<IUpdateClientDetails>>((patch) =>
    CompanyService.updateClientDetails(buildRecordRequest(patch)),
  );
  const draft = useWriteThrough<{ clientType: string | null }>((patch) =>
    CompanyService.saveOnboardingDraft({
      companyRowID,
      clientInformation: { clientType: patch.clientType },
    }),
  );

  useEffect(() => {
    // Two writers on this step — the client record and the onboarding draft — so the step reports a
    // save if either landed, and a failure if either failed.
    //
    // The flush is also the earliest signal that a save was attempted — the shell calls it before
    // either round trip — so the fields are marked here, synchronously, rather than two round trips
    // later when the shell's own verdict lands. The shell still has the final say on whether the
    // advance is allowed; this only stops a blank required field from taking seconds to say so.
    registerFlush(async () => {
      setSaveAttempted(true);
      return combineWriteResults(await record.flush(), await draft.flush());
    });
  }, [registerFlush, record, draft]);

  useEffect(() => {
    // The same rule the shell applies to the reloaded state after a save, given the values on
    // screen instead — so a step that cannot pass is refused before anything is written, and
    // refused immediately rather than two round trips later.
    //
    // Deregistered on the way out: the shell keeps whatever was last registered, so leaving this
    // behind would have step 2 judged by step 1's rules.
    registerValidate?.(() => stepValidationMessage(liveStateRef.current, 1));
    return () => registerValidate?.(null);
  }, [registerValidate]);

  /**
   * A complete payload every time: the endpoint writes each field it is given, so sending a partial
   * one would clear the fields only Details edits. The collections stay null, which the server
   * leaves untouched. Read from the form as last rendered, never from the scheduling render's copy.
   */
  const buildRecordRequest = (patch: Partial<IUpdateClientDetails>): IUpdateClientDetails => {
    const current = formRef.current;
    return {
      companyRowID,
      companyName: current.companyName,
      clientStatusID: state.clientStatusID ?? null,
      companyCode: current.companyCode || null,
      countryCode: current.countryCode || null,
      subDivisionCode: current.subDivisionCode || null,
      headOfficePhone: current.headOfficePhone || null,
      franchiseID: current.franchiseID === "" ? null : Number(current.franchiseID),
      franchiseOfficeID: current.franchiseOfficeID === "" ? null : Number(current.franchiseOfficeID),
      // Sent as an object on every step-1 write so the pick is what the form shows; the Details page
      // never sends it and so never disturbs it (story 6372).
      owningFranchise: {
        imperansFranchiseID: current.imperansFranchiseID,
        name: current.imperansFranchiseName,
      },
      companyType: state.companyType || null,
      leadTypeID: current.leadTypeID === "" ? null : Number(current.leadTypeID),
      industryID: current.industryID === "" ? null : Number(current.industryID),
      website: current.website || null,
      totalStaffNumber: current.totalStaffNumber === "" ? null : Number(current.totalStaffNumber),
      specialPricing: current.specialPricing,
      primaryCompanyRelationshipManager: state.primaryRelationshipManagerUser,
      vendorTypeIDs: null,
      serviceTypeIDs: null,
      healthTickPrimaryRelationshipManager: null,
      additionalCompanyRelationshipManagers: null,
      attachments: null,
      ...patch,
    };
  };

  const set = (changes: Partial<typeof form>, patch: Partial<IUpdateClientDetails> = {}) => {
    setForm((current) => ({ ...current, ...changes }));
    if (!frozen) record.schedule(patch);
  };

  /**
   * The owning Imperans franchise (story 6372): the list comes from the Imperans this CRM submits
   * to, re-fetched whenever the country changes, and the pick is written through like every other
   * step-1 field. Never filtered or replaced by the CRM's own region.
   */
  const franchiseList = useImperansReferenceList("franchises", form.countryCode);

  const onOwningFranchiseChange = (pick: IImperansReferencePick | null) => {
    set(
      { imperansFranchiseID: pick?.id ?? null, imperansFranchiseName: pick?.name ?? null },
      { owningFranchise: { imperansFranchiseID: pick?.id ?? null, name: pick?.name ?? null } },
    );
  };

  // The region may pre-select the franchise it maps to, once, while nothing is picked and only if
  // Imperans offers it (story 6372). It becomes an ordinary, visible pick the user can change.
  const suggested = useRef<boolean>(false);
  useEffect(() => {
    if (suggested.current || frozen || !franchiseList.loaded) return;
    if (form.imperansFranchiseID !== null) return;
    const suggestion = franchiseSuggestedByRegion(state.regionImperansFranchiseID, franchiseList.options);
    suggested.current = true;
    if (suggestion) onOwningFranchiseChange(pickFromOption(suggestion));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [franchiseList.loaded, franchiseList.options]);

  const onRegionChange = (value: string) => {
    const region = regions.find((x) => `${x.value}` === value);
    // Country prefills from the region only while it is blank, and is never overwritten once set
    // (S1.2) — a client may sit in a different country to its supporting branch.
    const country =
      form.countryCode ||
      (region?.countryName?.match(/zealand/i)
        ? "NZ"
        : region?.countryName?.match(/austral/i)
          ? "AU"
          : "");

    set(
      {
        franchiseID: value,
        countryCode: country,
      },
      {
        franchiseID: value === "" ? null : Number(value),
        countryCode: country || null,
      },
    );

    // The code follows the region while it is still auto-generated (S1.3, bug 5690).
    scheduleCodeRefresh();
  };

  const onCodeChange = (raw: string) => {
    const code = normaliseCompanyCode(raw);
    // Typing by hand permanently disables auto-generation for this client (S1.3).
    setAutoGenerated(false);
    set(
      { companyCode: code },
      { companyCode: code || null, companyCodeAutoGenerated: false },
    );
    setCodeMessage(
      code && !isCompanyCodeFormatValid(code) ? COMPANY_CODE_FORMAT_MESSAGE : "",
    );
    if (code && isCompanyCodeFormatValid(code)) checkCode(code);
  };

  const checkCode = (code: string) => {
    CompanyService.checkCompanyCodeAvailability(companyRowID, code).subscribe({
      next: (result) => setCodeMessage(result.isAvailable ? "" : result.message || ""),
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  /**
   * Flushes the pending save, then refreshes the code.
   *
   * The order matters: the code is derived from the stored name, country and region, so asking for it
   * before the write-through lands would return the value for the previous name (bug 5690).
   */
  const scheduleCodeRefresh = () => {
    if (frozen || !autoGenerated) return;
    void record.flush().then(() => refreshGeneratedCode());
  };

  /**
   * Pulls the code the server now derives, after a change to the name, country or region.
   *
   * The regeneration rule works — the stored code updates on save — but the field kept showing the
   * old value, so it looked as though only the Generate button worked (bug 5690). This asks for the
   * derived code the same way Generate does and shows it, and only while the code is still
   * auto-generated: once a character has been typed by hand the code never moves again (S1.3).
   *
   * A code that moved is also written through, as Generate writes it (TC-6348-03). The server
   * regenerates on the change itself, but any write already scheduled with the code on screen before
   * this answer carries the previous code, which the server reads as typed by hand — keeping it and
   * switching auto-generation off. Writing the refreshed code last, flagged as generated, makes the
   * stored code the one the field shows.
   *
   * The answer is dropped if the code was typed while it was on its way: writing it would overwrite
   * the typed code and flag it as generated again, which the server would store (S1.3).
   */
  const refreshGeneratedCode = () => {
    if (frozen || !autoGeneratedRef.current) return;

    const codeWhenAsked = formRef.current.companyCode;
    CompanyService.generateCompanyCode(companyRowID).subscribe({
      next: (result) => {
        if (!autoGeneratedRef.current || formRef.current.companyCode !== codeWhenAsked) return;
        if (!result?.companyCode) return;
        setCodeMessage("");
        if (formRef.current.companyCode === result.companyCode) return;
        setForm((current) => ({ ...current, companyCode: result.companyCode }));
        record.schedule({
          companyCode: result.companyCode,
          companyCodeAutoGenerated: true,
        });
      },
      // A failed refresh leaves the stored code alone; the next save derives it again.
      error: () => undefined,
    });
  };

  const onGenerate = () => {
    CompanyService.generateCompanyCode(companyRowID).subscribe({
      next: (result) => {
        setForm((current) => ({ ...current, companyCode: result.companyCode }));
        setCodeMessage("");
        // Generate replaces the code and re-enables auto-generation (S1.3).
        setAutoGenerated(true);
        record.schedule({
          companyCode: result.companyCode,
          companyCodeAutoGenerated: true,
        });
      },
      error: (error: AxiosError) => ToastService.axiosError(error),
    });
  };

  const onClientTypeChange = (clientType: string) => {
    setForm((current) => ({ ...current, clientType }));
    if (!frozen) draft.schedule({ clientType: clientType || null });
  };

  const regionCountry = regions.find((x) => `${x.value}` === `${form.franchiseID}`)?.countryName;

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          required
          label="Company Name"
          disabled={frozen}
          error={Boolean(fieldErrors.companyName)}
          helperText={fieldErrors.companyName || " "}
          value={form.companyName}
          onChange={(e) => set({ companyName: e.target.value }, { companyName: e.target.value })}
          // On blur, not per keystroke: the code follows the name, but rewriting it mid-word would
          // fight the typing (S1.3, bug 5690).
          onBlur={() => scheduleCodeRefresh()}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
          <TextField
            fullWidth
            size="small"
            required
            label="Company Code"
            disabled={frozen}
            value={form.companyCode}
            error={Boolean(codeMessage) || Boolean(fieldErrors.companyCode)}
            helperText={codeMessage || fieldErrors.companyCode || " "}
            onChange={(e) => onCodeChange(e.target.value)}
          />
          {/* Not rendered on a frozen record — a submitted onboarding never regenerates (S1.9). */}
          {!frozen && (
            <Button sx={{ mt: 0.5, whiteSpace: "nowrap" }} onClick={onGenerate}>
              Generate
            </Button>
          )}
        </Box>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          size="small"
          required
          label="TDDA Supporting Region"
          disabled={frozen}
          value={`${form.franchiseID}`}
          error={Boolean(fieldErrors.franchiseID)}
          helperText={
            fieldErrors.franchiseID ||
            (regionCountry ? `This region sits in ${regionCountry}.` : " ")
          }
          onChange={(e) => onRegionChange(e.target.value)}
        >
          {(regions.length ? regions : (supportiveRegions as any[])).map((region) => (
            <MenuItem key={region.value} value={`${region.value}`}>
              {region.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          size="small"
          required
          label="Country"
          disabled={frozen}
          value={form.countryCode}
          error={Boolean(fieldErrors.countryCode)}
          helperText={
            fieldErrors.countryCode ||
            "Country — not region — drives alcohol bands, lab lists, invoicing options and the code suffix."
          }
          onChange={(e) =>
            {
              const countryChanged = e.target.value !== form.countryCode;
              set(
                {
                  countryCode: e.target.value,
                  // The franchise list is per country: a new country re-fetches it and clears the
                  // pick, so a franchise from the other country is never carried across (story 6372).
                  ...(countryChanged ? { imperansFranchiseID: null, imperansFranchiseName: null } : {}),
                },
                {
                  countryCode: e.target.value || null,
                  ...(countryChanged ? { owningFranchise: { imperansFranchiseID: null, name: null } } : {}),
                },
              );
              // SSO exists only on Australian clients (bug 5845): leaving Australia clears it so
              // the field re-asks rather than holding a value the list no longer offers.
              if (
                form.clientType &&
                !clientTypesForCountry(state.references.clientTypes, e.target.value).includes(form.clientType)
              )
                onClientTypeChange("");
              // The code suffix follows the country while still auto-generated (S1.3, bug 5690).
              scheduleCodeRefresh();
            }
          }
        >
          {COUNTRY_OPTIONS.map((country) => (
            <MenuItem key={country.value} value={country.value}>
              {country.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Sub/Division Code"
          disabled={frozen}
          value={form.subDivisionCode}
          onChange={(e) => {
            const value = upperCaseAsTyped(e.target.value);
            set({ subDivisionCode: value }, { subDivisionCode: value || null });
          }}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        {/* The client-level TDDA Supporting Clinic left this step with Feature CRM3 (story 6373):
            the clinic is a per-site fact picked on step 3. The record's Details page still holds the
            CRM field. In its place, the franchise that will own the client in Imperans, from the
            Imperans this CRM submits to (story 6372). */}
        <ImperansReferencePicker
          label="Owning Imperans franchise"
          list={franchiseList}
          hasCountry={Boolean(form.countryCode)}
          value={
            form.imperansFranchiseID === null
              ? null
              : { id: form.imperansFranchiseID, name: form.imperansFranchiseName }
          }
          onChange={onOwningFranchiseChange}
          required
          disabled={frozen}
          error={Boolean(fieldErrors.owningFranchise)}
          helperText={
            fieldErrors.owningFranchise ||
            "Served live by Imperans for the client's country. The region may suggest it, but this pick is what is sent."
          }
        />
      </Grid>

      <Grid item xs={12}>
        {/* Required since 9 Sep 2026 (task 6007): asterisked, marked when empty and blocking the
            advance, like the other five. One value since D12 (story 6348): National, Regional or
            SSO — exactly what Imperans stores, and what decides whether it raises a review ticket. */}
        <FormControl error={Boolean(fieldErrors.clientType)}>
          <FormLabel required sx={{ fontSize: 13 }}>
            Client Type
          </FormLabel>
          <RadioGroup
            row
            value={form.clientType}
            onChange={(e) => onClientTypeChange(e.target.value)}
          >
            {clientTypesForCountry(state.references.clientTypes, form.countryCode).map((clientType) => (
              <FormControlLabel
                key={clientType}
                value={clientType}
                control={<Radio size="small" disabled={frozen} />}
                label={clientType}
              />
            ))}
          </RadioGroup>
          <Typography
            variant="caption"
            color={fieldErrors.clientType ? "error" : "text.secondary"}
          >
            {fieldErrors.clientType ||
              "Mirrored to the CRM's National/Regional field. A national client is reviewed by the Imperans setup team before activation."}
          </Typography>
        </FormControl>
      </Grid>

      <Grid item xs={12} md={6}>
        {/* Required: Imperans creates no client without one of its industry codes, and the CRM's
            own pre-submit check (S6) refuses the request without it — so it is asterisked, marked
            when empty and blocks the advance like the other required fields. */}
        <TextField
          select
          fullWidth
          size="small"
          required
          label="Industry"
          disabled={frozen}
          error={Boolean(fieldErrors.industryID)}
          helperText={fieldErrors.industryID || " "}
          value={`${form.industryID}`}
          onChange={(e) =>
            set(
              { industryID: e.target.value },
              { industryID: e.target.value === "" ? null : Number(e.target.value) },
            )
          }
        >
          {(industries as any[]).map((industry) => (
            <MenuItem key={industry.value} value={`${industry.value}`}>
              {industry.label}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Head Office Phone"
          disabled={frozen}
          value={form.headOfficePhone}
          onChange={(e) =>
            set({ headOfficePhone: e.target.value }, { headOfficePhone: e.target.value || null })
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          label="Website"
          placeholder="https://"
          disabled={frozen}
          value={form.website}
          onChange={(e) => set({ website: e.target.value }, { website: e.target.value || null })}
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          size="small"
          type="number"
          label="Total Employee Numbers"
          disabled={frozen}
          value={form.totalStaffNumber}
          // A head count has no negative. This field is a plain TextField rather than a
          // ViewInputControl, so it carries its own refusal of the minus key and a pasted negative.
          inputProps={{ min: 0 }}
          onKeyDown={(e) => {
            if (["-", "+", "e", "E"].includes(e.key)) e.preventDefault();
          }}
          onPaste={(e) => {
            if (e.clipboardData.getData("text").includes("-")) e.preventDefault();
          }}
          onChange={(e) =>
            set(
              { totalStaffNumber: e.target.value },
              { totalStaffNumber: e.target.value === "" ? null : Number(e.target.value) },
            )
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          select
          fullWidth
          size="small"
          label="Lead Type"
          disabled={frozen}
          value={`${form.leadTypeID}`}
          onChange={(e) =>
            set(
              { leadTypeID: e.target.value },
              { leadTypeID: e.target.value === "" ? null : Number(e.target.value) },
            )
          }
        >
          {(leadTypeList as any[])
            .filter((x) => x.isActive || `${x.value}` === `${form.leadTypeID}`)
            .map((leadType) => (
              <MenuItem key={leadType.value} value={`${leadType.value}`}>
                {leadType.label}
              </MenuItem>
            ))}
        </TextField>
      </Grid>

      <Grid item xs={12} md={6}>
        {/* The existing CRM relationship-manager list, edited on Details (decision 3). */}
        <TextField
          fullWidth
          size="small"
          required
          label="TDDA Primary Relationship Manager"
          disabled
          value={state.primaryRelationshipManager || ""}
          error={Boolean(fieldErrors.primaryRelationshipManager)}
          helperText={
            fieldErrors.primaryRelationshipManager ||
            "Managed on the client record's Details page."
          }
        />
      </Grid>

      <Grid item xs={12} md={6}>
        <FormControl>
          <FormLabel sx={{ fontSize: 13 }}>Special Pricing</FormLabel>
          <RadioGroup
            row
            value={form.specialPricing ? "yes" : "no"}
            onChange={(e) =>
              set(
                { specialPricing: e.target.value === "yes" },
                { specialPricing: e.target.value === "yes" },
              )
            }
          >
            <FormControlLabel value="yes" control={<Radio disabled={frozen} size="small" />} label="Yes" />
            <FormControlLabel value="no" control={<Radio disabled={frozen} size="small" />} label="No" />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid item xs={12}>
        <Alert severity="info">
          Addresses are not captured here — every address is a site on step 3, the first of them
          doubling as the head office. Status and Sales Pipeline Value live on the record's Details
          page.
        </Alert>
      </Grid>
    </Grid>
  );
};

export default observer(Step1ClientInformation);
